import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';
import { extractApiErrorMessage } from '@/lib/api/extract-api-error-message';
import type {
  DesktopPluginCreateRequest,
  DesktopPluginItem,
  DesktopPluginUploadQuery,
} from '@cms/models';
import { desktopPluginsApi, useUpdateDesktopPlugin } from '@/lib/api/desktop-plugins.api';
import { sha256FileHex } from '@/lib/upload/presigned-put';
import { cn } from '@/lib/utils';
import { CommonModal } from '@/components/common/CommonModal';
import { Toggle } from '@/components/ui/toggle';

const PLUGIN_KEY_PRESETS = ['ffmpeg', 'ffprobe', 'ffmpge'] as const;

type PluginFormData = {
  appSlug: string;
  pluginKey: string;
  platform: 'win32' | 'darwin' | 'linux';
  arch: 'x64' | 'arm64' | '';
  description: string;
  status: 'active' | 'inactive';
};

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20';
const textareaClass =
  'mt-1 block w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20';

export type PluginFormModalMode = 'create' | 'view' | 'edit';

type PluginFormModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly mode: PluginFormModalMode;
  readonly plugin: DesktopPluginItem | null;
  readonly defaultAppSlug?: string;
};

export function PluginFormModal({
  open,
  onClose,
  onSuccess,
  mode,
  plugin,
  defaultAppSlug = 'veo3-pro',
}: PluginFormModalProps) {
  const formId = 'desktop-plugin-form';
  const isCreate = mode === 'create';
  const isView = mode === 'view';
  const updateMutation = useUpdateDesktopPlugin();
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = useForm<PluginFormData>({
    defaultValues: {
      appSlug: defaultAppSlug,
      pluginKey: 'ffmpeg',
      platform: 'win32',
      arch: '',
      description: '',
      status: 'active',
    },
  });
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      reset();
      setFile(null);
      setUploadProgress(null);
      return;
    }
    if (plugin) {
      reset({
        appSlug: plugin.appSlug,
        pluginKey: plugin.pluginKey,
        platform: plugin.platform,
        arch: (plugin.arch ?? '') as 'x64' | 'arm64' | '',
        description: plugin.description ?? '',
        status: plugin.status ?? 'inactive',
      });
      return;
    }
    reset({
      appSlug: defaultAppSlug,
      pluginKey: 'ffmpeg',
      platform: 'win32',
      arch: '',
      description: '',
      status: 'active',
    });
    setFile(null);
  }, [open, plugin, defaultAppSlug, reset]);

  const onSubmit = async (data: PluginFormData) => {
    try {
      if (isView) return;
      const pluginKey = data.pluginKey.trim();
      if (!pluginKey) {
        toast.error('Plugin key is required');
        return;
      }

      if (!isCreate) {
        if (!plugin?.id) {
          toast.error('Missing plugin id');
          return;
        }
        const payload: {
          description?: string;
          objectKey?: string;
          fileName?: string;
          sizeBytes?: number;
          sha256Hex?: string;
        } = {
          description: data.description.trim() || '',
        };
        if (file) {
          const uploadQuery: DesktopPluginUploadQuery = {
            appSlug: data.appSlug.trim(),
            pluginKey,
            platform: data.platform,
            arch: data.arch || undefined,
            fileName: file.name,
          };
          setUploadProgress(0);
          const upload = await desktopPluginsApi.uploadArtifact(uploadQuery, file, setUploadProgress);
          payload.objectKey = upload.objectKey;
          payload.fileName = file.name;
          payload.sizeBytes = file.size;
          payload.sha256Hex = await sha256FileHex(file);
          setUploadProgress(null);
        }
        await updateMutation.mutateAsync({ pluginId: plugin.id, payload });

        if (plugin.status !== data.status) {
          if (data.status === 'active') {
            await desktopPluginsApi.publishPlugin(plugin.id);
          } else {
            await desktopPluginsApi.unpublishPlugin(plugin.id);
          }
        }
        toast.success('Plugin updated successfully');
        onSuccess();
        onClose();
        return;
      }

      if (!file) {
        toast.error('Please select plugin executable file');
        return;
      }

      const uploadQuery: DesktopPluginUploadQuery = {
        appSlug: data.appSlug.trim(),
        pluginKey,
        platform: data.platform,
        arch: data.arch || undefined,
        fileName: file.name,
      };
      setUploadProgress(0);
      const upload = await desktopPluginsApi.uploadArtifact(uploadQuery, file, setUploadProgress);
      const sha256Hex = await sha256FileHex(file);
      setUploadProgress(null);

      const createPayload: DesktopPluginCreateRequest = {
        appSlug: uploadQuery.appSlug,
        pluginKey: uploadQuery.pluginKey,
        platform: uploadQuery.platform,
        arch: uploadQuery.arch ?? undefined,
        objectKey: upload.objectKey,
        fileName: uploadQuery.fileName,
        sizeBytes: file.size,
        sha256Hex,
        description: data.description.trim() || undefined,
      };

      const created = await desktopPluginsApi.createPlugin(createPayload);
      if (!created.id) {
        throw new Error('Missing created plugin id');
      }
      if (data.status === 'active') {
        await desktopPluginsApi.publishPlugin(created.id);
      }

      toast.success('Plugin uploaded successfully');
      onSuccess();
      onClose();
    } catch (error: unknown) {
      setUploadProgress(null);
      toast.error(extractApiErrorMessage(error));
    }
  };

  const title =
    mode === 'create' ? 'Upload plugin' : mode === 'view' ? 'Plugin details' : 'Edit plugin';
  const isReadOnlyFields = !isCreate;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      panelClassName="max-w-2xl"
      overlayClassName="p-0"
      footer={
        isView ? (
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button form={formId} type="submit" disabled={isSubmitting || updateMutation.isPending}>
              {isCreate
                ? isSubmitting
                  ? 'Uploading…'
                  : 'Upload & create'
                : updateMutation.isPending
                  ? 'Saving…'
                  : 'Save'}
            </Button>
          </>
        )
      }
      footerClassName="flex justify-end gap-2"
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="plugin-app-slug" className="block text-sm font-medium text-foreground">
              App slug
            </label>
            <input
              id="plugin-app-slug"
              disabled={isReadOnlyFields}
              {...register('appSlug')}
              className={cn(inputClass, isReadOnlyFields && 'cursor-default bg-muted/50')}
            />
          </div>
          <div>
            <label htmlFor="plugin-key" className="block text-sm font-medium text-foreground">
              Plugin key
            </label>
            {isReadOnlyFields ? (
              <input
                id="plugin-key"
                disabled
                {...register('pluginKey')}
                className={cn(inputClass, 'cursor-default bg-muted/50')}
              />
            ) : (
              <>
                <input
                  id="plugin-key"
                  list="plugin-key-presets"
                  placeholder="e.g. ffmpeg"
                  {...register('pluginKey')}
                  className={inputClass}
                />
                <datalist id="plugin-key-presets">
                  {PLUGIN_KEY_PRESETS.map(key => (
                    <option key={key} value={key} />
                  ))}
                </datalist>
              </>
            )}
          </div>

          <div>
            <label htmlFor="plugin-platform" className="block text-sm font-medium text-foreground">
              Platform
            </label>
            <select
              id="plugin-platform"
              disabled={isReadOnlyFields}
              {...register('platform')}
              className={cn(inputClass, isReadOnlyFields && 'cursor-default bg-muted/50')}
            >
              <option value="win32">win32</option>
              <option value="darwin">darwin</option>
              <option value="linux">linux</option>
            </select>
          </div>
          <div>
            <label htmlFor="plugin-arch" className="block text-sm font-medium text-foreground">
              Arch (optional)
            </label>
            <select
              id="plugin-arch"
              disabled={isReadOnlyFields}
              {...register('arch')}
              className={cn(inputClass, isReadOnlyFields && 'cursor-default bg-muted/50')}
            >
              <option value="">(any)</option>
              <option value="x64">x64</option>
              <option value="arm64">arm64</option>
            </select>
          </div>

          <div>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <div className="space-y-1 mt-2">
                  <Toggle
                    id="plugin-status"
                    label="Status"
                    checked={field.value === 'active'}
                    disabled={isView}
                    onCheckedChange={checked => field.onChange(checked ? 'active' : 'inactive')}
                  />
                  <div className="text-sm text-muted-foreground">
                    {field.value === 'active' ? 'Active' : 'Inactive'}
                  </div>
                </div>
              )}
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="plugin-description" className="block text-sm font-medium text-foreground">
              Description (optional)
            </label>
            <textarea
              id="plugin-description"
              rows={2}
              disabled={isView}
              {...register('description')}
              className={cn(textareaClass, isView && 'cursor-default bg-muted/50')}
            />
          </div>

          {(isCreate || mode === 'edit') && (
            <div className="sm:col-span-2">
              <label htmlFor="plugin-file" className="block text-sm font-medium text-foreground">
                {isCreate ? 'Executable file' : 'Replace file (optional)'}
              </label>
              <input
                id="plugin-file"
                type="file"
                disabled={isView}
                onChange={e => setFile(e.target.files?.[0] ?? null)}
                className="mt-1 block w-full text-sm text-muted-foreground"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {file
                  ? `Selected: ${file.name} (${Math.round(file.size / 1024 / 1024)} MB)`
                  : 'No file selected'}
              </p>
              {uploadProgress != null && (
                <div className="mt-2 space-y-1">
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary transition-[width] duration-150"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Uploading… {uploadProgress}%</p>
                </div>
              )}
            </div>
          )}
        </div>
      </form>
    </CommonModal>
  );
}
